import { useCallback, useRef } from 'react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../redux/store';
import { useLayoutPreferences } from '../../hooks/useLayoutPreferences';
import ChatView from './ChatView';
// DELETED: import CourtResearchView from './CourtResearchView';
import Sidebar from './Sidebar';
import Knowledgebase from '../knowledgebase/knowledgebase';
import LegalNewsFeed from '../legal-news/LegalNewsFeed';
import UrlSync from '../common/UrlSync';

/**
 * Main App component with draggable resizable panels
 */
const MainView = () => {
  const activeSidebarTab = useAppSelector((state) => state.UIReducer.activeSidebarTab);
  const currentCaseId = useAppSelector((state) => state.CaseReducer.currentCaseId);
  const showLegalNewsFeed = useAppSelector((state) => state.UserReducer.user?.showLegalNewsFeed);
  const {
    sidebarWidth,
    knowledgebaseWidth,
    minWidths,
    updateLayoutPreferences,
  } = useLayoutPreferences();

  // Track resize timers to detect when user stops dragging
  const sidebarResizeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const knowledgebaseResizeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle sidebar resize - debounced to only save when user stops dragging
  const handleSidebarResize = useCallback(
    (size: number) => {
      // Clear existing timer
      if (sidebarResizeTimerRef.current) {
        clearTimeout(sidebarResizeTimerRef.current);
      }

      // Set new timer - update preferences when user stops resizing (100ms after last resize event)
      sidebarResizeTimerRef.current = setTimeout(() => {
        updateLayoutPreferences({
          sidebarWidth: size,
        });
      }, 100);
    },
    [updateLayoutPreferences]
  );

  // Handle knowledgebase resize - debounced to only save when user stops dragging
  const handleKnowledgebaseResize = useCallback(
    (size: number) => {
      // Clear existing timer
      if (knowledgebaseResizeTimerRef.current) {
        clearTimeout(knowledgebaseResizeTimerRef.current);
      }

      // Set new timer - update preferences when user stops resizing (100ms after last resize event)
      knowledgebaseResizeTimerRef.current = setTimeout(() => {
        updateLayoutPreferences({
          knowledgebaseWidth: size,
        });
      }, 100);
    },
    [updateLayoutPreferences]
  );

  // Convert pixel min widths to percentages for react-resizable-panels
  // We'll use a rough estimate: assume 1400px screen width for conversion
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1400;
  const minSidebarPercent = (minWidths.sidebar / screenWidth) * 100;
  const minKnowledgebasePercent = (minWidths.knowledgebase / screenWidth) * 100;

  // Calculate main content width (remaining space)
  const mainContentWidth = 100 - sidebarWidth - knowledgebaseWidth;

  return (
    <PanelGroup
      direction="horizontal"
      className="h-screen w-full overflow-hidden"
      style={{ height: '100vh', maxHeight: '100vh' }}
    >
      <UrlSync />
      {/* Sidebar Panel */}
      <Panel
        defaultSize={sidebarWidth}
        minSize={minSidebarPercent}
        collapsible={false}
        onResize={handleSidebarResize}
      >
        <div className="h-full">
          <Sidebar />
        </div>
      </Panel>

      {/* Resize Handle between Sidebar and Main Content */}
      <PanelResizeHandle className="w-1 bg-border hover:bg-border/80 transition-colors cursor-col-resize group">
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-0.5 h-8 bg-border group-hover:bg-gold/50 transition-colors rounded" />
        </div>
      </PanelResizeHandle>

      {/* Main Content Panel */}
      <Panel
        defaultSize={mainContentWidth}
        minSize={Math.max(400 / screenWidth * 100, 10)} // 400px minimum or 10% whichever is larger
      >
        <div className="h-full">
          <ChatView />
        </div>
      </Panel>

      {/* Resize Handle between Main Content and Knowledgebase */}
      <PanelResizeHandle className="w-1 bg-border hover:bg-border/80 transition-colors cursor-col-resize group">
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-0.5 h-8 bg-border group-hover:bg-gold/50 transition-colors rounded" />
        </div>
      </PanelResizeHandle>

      {/* Knowledgebase or Legal News Panel */}
      <Panel
        defaultSize={knowledgebaseWidth}
        minSize={minKnowledgebasePercent}
        collapsible={false}
        onResize={handleKnowledgebaseResize}
      >
        <div className="h-full bg-background">
          {!currentCaseId && showLegalNewsFeed ? (
            <LegalNewsFeed />
          ) : (
            <Knowledgebase />
          )}
        </div>
      </Panel>
    </PanelGroup>
  );
};

export default MainView;
